// components/CalendarLegend.jsx

import React, { useMemo } from "react";
import {
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

import {
  CALENDAR_EVENT_PALETTES,
  getEventPaletteKey,
} from "../data/calendarEventColors";

export default function CalendarLegend({
  events = [],
  hiddenPaletteKeys = [],
  onTogglePalette,
  compact = false,
}) {
  const entries = useMemo(() => {
    const counts = events.reduce((accumulator, event) => {
      const paletteKey = getEventPaletteKey(event);

      accumulator[paletteKey] =
        (accumulator[paletteKey] || 0) + 1;

      return accumulator;
    }, {});

    return Object.values(
      CALENDAR_EVENT_PALETTES
    )
      .filter(
        (palette) =>
          counts[palette.key] > 0 ||
          hiddenPaletteKeys.includes(palette.key)
      )
      .map((palette) => ({
        ...palette,
        count: counts[palette.key] || 0,
      }));
  }, [events, hiddenPaletteKeys]);

  if (entries.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!compact && (
        <span className="mr-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          Afficher
        </span>
      )}

      {entries.map((palette) => {
        const hidden =
          hiddenPaletteKeys.includes(palette.key);

        return (
          <button
            key={palette.key}
            type="button"
            onClick={() =>
              onTogglePalette?.(palette.key)
            }
            title={
              hidden
                ? `Afficher ${palette.label}`
                : `Masquer ${palette.label}`
            }
            className={[
              "inline-flex min-h-8 items-center gap-2 rounded-full border px-2.5 py-1",
              "transition duration-200",
              "border-[var(--calendar-border)]",
              "bg-[var(--calendar-background)]",
              "text-[var(--calendar-text)]",
              "dark:border-[var(--calendar-dark-border)]",
              "dark:bg-[var(--calendar-dark-background)]",
              "dark:text-[var(--calendar-dark-text)]",
              hidden
                ? "opacity-40 grayscale hover:opacity-70"
                : "hover:-translate-y-0.5 hover:shadow-sm",
            ].join(" ")}
            style={{
              "--calendar-background":
                palette.lightBackground,
              "--calendar-border":
                palette.lightBorder,
              "--calendar-text": palette.lightText,

              "--calendar-dark-background":
                palette.darkBackground,
              "--calendar-dark-border":
                palette.darkBorder,
              "--calendar-dark-text":
                palette.darkText,

              "--calendar-accent": palette.accent,
            }}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--calendar-accent)]"
              aria-hidden="true"
            />

            <span className="max-w-[150px] truncate text-[10px] font-black sm:text-[11px]">
              {palette.label}
            </span>

            <span className="rounded-full bg-white/60 px-1.5 py-0.5 text-[9px] font-black dark:bg-black/20">
              {palette.count}
            </span>

            {hidden ? (
              <FaEyeSlash className="text-[10px]" />
            ) : (
              <FaEye className="text-[10px] opacity-60" />
            )}
          </button>
        );
      })}
    </div>
  );
}